"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import AuthCard from "@/components/auth/auth-card";
import Button from "@/components/ui/button";
import FormControl from "@/components/ui/form-control";
import FormGroup from "@/components/ui/form-group";
import InputText from "@/components/ui/input-text";
import { useFormContext } from "@/providers/form.provider";
import { useToast } from "@/providers/toast.provider";
import type { UserDetails } from "@/types/containers/auth/signup";
import { apiClient } from "@/utils/api/api-client";
import type { SignupValues } from "@/utils/validations/auth/signup";

const SignUpCard: React.FC = () => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { setValue, setError } = useFormContext<UserDetails>();

  const { showToast } = useToast();

  return (
    <AuthCard isLoading={isLoading} heading="Sign Up" className="!max-w-md">
      {/* Form */}
      <FormGroup
        onSubmit={(values, errors) => {
          if (errors) {
            return;
          }

          setIsLoading(true);
          apiClient
            .post<{ success: boolean; message: string }, SignupValues>(
              "/auth/signup",
              values as SignupValues,
            )
            .then((resp) => {
              if (resp?.success) {
                showToast("Account created successfully!", {
                  type: "success",
                });
                setValue("companyName", "");
                setValue("phone", "");
                setValue("email", "");
                setTimeout(() => {
                  router.replace("/login");
                }, 2000);
              } else if (resp?.message) {
                showToast(resp?.message, {
                  type: "error",
                });
              }
            })
            .catch((e: ApiError) => {
              const errors = e.error?.errors as { [key: string]: string };
              if (errors?.email) {
                setError("email", {
                  message: errors.email,
                });
              } else if (errors?.phone) {
                setError("phone", {
                  message: errors.phone,
                });
              } else {
                showToast(e.error?.message, {
                  type: "error",
                });
              }
            })
            .finally(() => {
              setIsLoading(false);
            });
        }}
      >
        <FormControl label="Company Name" name="companyName" isRequired>
          <InputText />
        </FormControl>

        <FormControl isRequired label="Phone Number" name="phone">
          <InputText />
        </FormControl>

        <FormControl isRequired label="Email ID" name="email">
          <InputText />
        </FormControl>

        {/* Submit */}
        <Button fullWidth size="lg">
          Sign Up
        </Button>
      </FormGroup>

      {/* Login */}
      <p className="text-center text-sm text-slate-600">
        Already have an account?{" "}
        <Link
          href="/login"
          className="font-medium text-[#1E6FD9] hover:underline"
        >
          Login
        </Link>
      </p>
    </AuthCard>
  );
};

export default SignUpCard;
